import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Plus, Trash, UsersThree, CircleNotch } from "@phosphor-icons/react";
import { api, errDetail } from "../lib/api";
import { Sidebar } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";

export default function MemoryPage() {
  const { user } = useAuth();
  const [about, setAbout] = useState("");
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [personal, setPersonal] = useState([]);
  const [team, setTeam] = useState([]);
  const [newMem, setNewMem] = useState("");
  const [newTeamMem, setNewTeamMem] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const [p, m] = await Promise.all([api.get("/profile"), api.get("/memories")]);
    setAbout(p.data.about);
    setInstructions(p.data.instructions);
    setPersonal(m.data.personal);
    setTeam(m.data.team);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { about, instructions });
      toast.success("Profile saved — every model will now know this");
    } catch (e) { toast.error(errDetail(e)); } finally { setSaving(false); }
  };

  const addMemory = async (scope) => {
    const content = scope === "team" ? newTeamMem : newMem;
    if (!content.trim()) return;
    try {
      await api.post("/memories", { content, scope });
      scope === "team" ? setNewTeamMem("") : setNewMem("");
      toast.success("Memory added");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const deleteMemory = async (id) => {
    try {
      await api.delete(`/memories/${id}`);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const inputCls = "w-full bg-[var(--bg)] border border-[var(--border-strong)] rounded-xl px-3.5 py-2.5 text-sm text-[var(--text)] outline-none focus:border-[var(--accent)] transition-colors duration-300 placeholder:text-[var(--muted)]";

  const MemoryList = ({ items, canDelete, testPrefix }) => (
    <div className="space-y-2">
      {items.length === 0 && <p className="text-sm text-[var(--muted)]">Nothing remembered yet.</p>}
      {items.map((m) => (
        <div key={m.id} data-testid={`${testPrefix}-${m.id}`} className="group flex items-start gap-3 bg-[var(--surface)] border border-[var(--border)] rounded-xl px-4 py-3">
          <p className="flex-1 text-[13px] text-[var(--text)] leading-relaxed">{m.content}</p>
          <span className="text-[9px] uppercase tracking-wider text-[var(--muted)] mt-0.5">{m.source === "auto" ? "auto" : "manual"}</span>
          {canDelete && (
            <button data-testid={`delete-memory-${m.id}`} onClick={() => deleteMemory(m.id)} title="Forget"
              className="opacity-0 group-hover:opacity-100 md:opacity-0 opacity-100 transition-opacity duration-300 text-[var(--muted)] hover:text-[var(--error)] p-0.5 rounded">
              <Trash size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex h-screen bg-[var(--bg)] overflow-hidden">
      <Sidebar />
      <main className="flex-1 overflow-y-auto px-6 md:px-10 py-10">
        <h1 className="font-serif-display font-semibold text-3xl sm:text-4xl tracking-tight mb-2">Memory</h1>
        <p className="text-sm text-[var(--text-2)] mb-8">Everything here is shared with all models — Claude, ChatGPT and Gemini — in every chat.</p>

        <div className="max-w-2xl space-y-10">
          <section className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6" style={{ boxShadow: "var(--shadow)" }} data-testid="profile-section">
            <h2 className="font-serif-display font-semibold text-lg mb-4">Your profile</h2>
            <label className="text-xs font-medium text-[var(--text-2)] block mb-1.5">About you (role, company, projects)</label>
            <textarea data-testid="profile-about-input" rows={3} className={`${inputCls} resize-none mb-4`} value={about}
              onChange={(e) => setAbout(e.target.value)} placeholder="e.g. I'm Yash from Exotic India. We export murtis and handicrafts…" />
            <label className="text-xs font-medium text-[var(--text-2)] block mb-1.5">Custom instructions (how models should answer)</label>
            <textarea data-testid="profile-instructions-input" rows={3} className={`${inputCls} resize-none mb-4`} value={instructions}
              onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Always be concise. Use inches for dimensions. Reply in simple English…" />
            <button data-testid="save-profile-button" onClick={saveProfile} disabled={saving}
              className="rounded-full text-white text-[13px] font-semibold px-6 py-2.5 transition-colors duration-300 disabled:opacity-50" style={{ background: "var(--accent)" }}>
              {saving ? <CircleNotch size={14} className="animate-spin inline" /> : "Save profile"}
            </button>
          </section>

          <section data-testid="personal-memories-section">
            <div className="flex items-center gap-2 mb-1">
              <Brain size={18} className="text-[var(--accent)]" />
              <h2 className="font-serif-display font-semibold text-lg">Personal memories</h2>
            </div>
            <p className="text-[12.5px] text-[var(--text-2)] mb-4">Added automatically as you chat, or add your own. Delete anything you want forgotten.</p>
            <div className="flex gap-2 mb-4">
              <input data-testid="new-memory-input" className={inputCls} placeholder="Add something to remember…" value={newMem}
                onChange={(e) => setNewMem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMemory("personal")} />
              <button data-testid="add-memory-button" onClick={() => addMemory("personal")}
                className="shrink-0 rounded-full text-white p-2.5 transition-colors duration-300" style={{ background: "var(--accent)" }}><Plus size={16} weight="bold" /></button>
            </div>
            <MemoryList items={personal} canDelete testPrefix="memory-item" />
          </section>

          <section data-testid="team-memories-section">
            <div className="flex items-center gap-2 mb-1">
              <UsersThree size={18} className="text-[var(--accent)]" />
              <h2 className="font-serif-display font-semibold text-lg">Team memory</h2>
            </div>
            <p className="text-[12.5px] text-[var(--text-2)] mb-4">Shared facts every team member's chats know. {isAdmin ? "You manage these as admin." : "Managed by your admin."}</p>
            {isAdmin && (
              <div className="flex gap-2 mb-4">
                <input data-testid="new-team-memory-input" className={inputCls} placeholder="e.g. Our company is Exotic India, we export handicrafts…" value={newTeamMem}
                  onChange={(e) => setNewTeamMem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMemory("team")} />
                <button data-testid="add-team-memory-button" onClick={() => addMemory("team")}
                  className="shrink-0 rounded-full text-white p-2.5 transition-colors duration-300" style={{ background: "var(--accent)" }}><Plus size={16} weight="bold" /></button>
              </div>
            )}
            <MemoryList items={team} canDelete={isAdmin} testPrefix="team-memory-item" />
          </section>
        </div>
      </main>
    </div>
  );
}
